import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { createCelloSignUpEvent, getTeamMemberServiceSide, getUserEmailByUcc, updateTeamMemberPublic, userLogin, verifyLogin } from '@services/youtube-platform.service';
import {
  setAuth,
  getAuth,
  isAuthenticated,
  setBanner,
  setUniversalLoggedInCookie,
  setCurrentTeamToken,
} from '@services/identity.service';
import useClientInfo from '@lib/hooks/useClientInfo';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { createCheckoutSession } from '@services/billing-service';
import { getDeeplink } from '@services/zoom';

import { isZoomApp, zoomAppBasePath } from '@zoom-app/helpers';
import { ZOOMAPP_CTX_PARAM } from '@lib/zoom/constants';
import ExternalLink from '@zoom-app/atoms/ExternalLink';
import { signupLink } from '@services/utils.service';
import Toast from '@molecules/Toast';
import Text from '@atoms/Text';
import { pushDataLayerForEvent } from '@lib/gtag';
import Button from '@atoms/new/button';
import Input from '@atoms/Input';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import yupValidator from '@lib/yup-validator';
import Image from 'next/image';
import useCountdownTimer from '@lib/hooks/useCountdownTimer';
import { getDeepLinkErrorRedirectUrl } from '@lib/zoom';

import Link from 'next/link';
import Loader from '@atoms/Atom/Loader';
import { ImageSlider } from './ImageSlider';

import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import {
  verifyGoogleAccessToken,
  verifyGoogleOneTapToken,
} from '@services/login/google';
import { usePostHog } from 'posthog-js/react';
import { useSearchParams } from 'next/navigation';
import { useOffice } from '@components/OfficeLoader';
import { useIsFirstRender } from '@uidotdev/usehooks';
import { getPresentationSettings } from '@components/Presentation/constants';
import { broadcastAuthState } from '@store/teamsStore';
import {
  ArrowLeftIcon as ArrowBackIcon,
  GoogleLogoIcon as GoogleIcon,
  InfoIcon as InfoOutlinedIcon
} from '@phosphor-icons/react';

type CustomError = {
  type?: 'email' | 'code';
  message?: string;
};

interface ReferralEvent {
  ucc: string | null;
  referralEmail: string | null;
  referralID: string | null;
}

interface LoginProps {
  heading: string;
  buttonText: string;
  accessType: 'login' | 'signup';
  isPopup: boolean;
}
const Login = ({
  heading = 'Log In',
  buttonText = 'Get login code',
  accessType = 'login',
  isPopup = false,
}: LoginProps) => {
  const router = useRouter();
  const clientInfo = useClientInfo();
  const posthog = usePostHog();
  const { isPowerpoint, isPowerpointDesktop, getLastRoutePath } = useOffice();
  const [mainButtonText, setMainButtonText] = useState(buttonText);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isAppSumoUser, setIsAppSumoUser] = useState(false);
  const [appSumoPlan, setAppSumoPlan] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [customError, setCustomError] = useState<CustomError>({});
  const [teamInvite, setTeamInvite] = useState<any>('');
  const { count, startTimer, isRunning, resetTimer } = useCountdownTimer();

  const { initTeam, ownTeam, user } = useTeamsData((s) => ({ initTeam: s.initTeam, ownTeam: s.ownTeam, user: s.user }));

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setFocus,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: router.query?.email,
    },
    resolver: yupValidator(
      yup.object().shape({
        email: yup
          .string()
          .email('Please enter a valid email.')
          .matches(/@[^.]*\./, 'Please enter a valid email.')
          .required('Please enter your email.'),
      })
    ),
  });
  const {
    register: codeRegister,
    handleSubmit: codeHandleSubmit,
    setValue,
    watch: codeWatch,
    setFocus: codeSetFocus,
    clearErrors: codeClearErrors,
    formState: { errors: codeErrors },
  } = useForm({
    resolver: yupValidator(
      yup.object().shape({
        inputCode: yup
          .string()
          .min(6, 'Please enter a valid code.')
          .max(6, 'Please enter a valid code.')
          .matches(/^\d+$/, 'Please enter a valid code.')
          .required('Please enter your code.'),
      })
    ),
  });

  const email = watch('email');

  useEffect(() => {
    if (showCodeInput) {
      setShowCodeInput(false);
    }
    if (!customError) return;
    setCustomError({});
    resetTimer();
    setValue('inputCode', '');
    setMainButtonText(buttonText);
    codeClearErrors();
  }, [email]);

  useEffect(() => {
    setCustomError({});
  }, [codeWatch('inputCode')]);

  const isFirstRender = useIsFirstRender();
  const searchParams = useSearchParams();
  const [referralDetails, setReferralDetails] = useState({
    ucc: '',
    email: '',
    referralID: '',
  });

  useEffect(() => {
    const fetchAndAcceptInvite = async () => {
      if (!searchParams?.get('invite')) return;

      try {
        const result = await getTeamMemberServiceSide(
          searchParams.get('id'),
          searchParams.get('id')
        );

        if (!result.status) {
          setTeamInvite('invalid');
          return;
        }

        const teamInfo = result.entity;
        setTeamInvite(teamInfo);

        if (!user) return;

        const payload = { accepted: true, user_id: user.id };
        const response = await updateTeamMemberPublic(
          teamInfo.id,
          teamInfo.billing_account.id,
          payload
        );

        if (response.status && response.entity.token) {
          await setCurrentTeamToken(response.entity.token);
          await initTeam();
        }
      } catch (err) {
        console.error('Error processing team invite:', err);
      }
    };

    fetchAndAcceptInvite();
  }, [user, searchParams]);

  useEffect(() => {
    if (!isFirstRender) return;

    (async () => {
      const referralID = searchParams?.get('referral') || '';

      let event: ReferralEvent = {
        ucc: null,
        referralID,
        referralEmail: null,
      };

      const ucc = searchParams?.get('ucc') || '';

      if (ucc) {
        const refferalEmail = await getUserEmailByUcc(ucc);
        const email = refferalEmail?.entity?.email;

        event.ucc = ucc;
        event.referralEmail = email;

        setReferralDetails({
          ucc,
          email,
          referralID,
        });
      }

      if (referralID || ucc) {
        posthog?.capture('Refferal Link Detected', {
          ReferrerUcc: event.ucc,
          ReferrerId: event.referralID,
          ReferrerEmail: event.referralEmail,
        });
      }
    })();
  }, []);

  const handleLoginSuccess = async (auth: any, isGoogleSignUp: boolean = false) => {
    posthog?.identify(auth.id, {
      email: auth.email,
    });
    posthog?.alias(auth.email, auth.id);
    try {
      const ucc = await window.CelloAttribution('getUcc');
      if (ucc) {
        createCelloSignUpEvent({ userId: auth.id, email: auth.email, ucc });
      }
    } catch (error) {
      console.log('Failed to fetch token ', { error });
    }
    if (auth?.isNewUser) {
      posthog?.capture('Signed Up', {
        isZoom: searchParams?.get('utm_source') === 'zoomapp',
        isReferral: !!referralDetails.ucc || !!referralDetails.referralID,
        RefererId: referralDetails.referralID,
        RefererEmail: referralDetails.email,
        ReferrerUcc: referralDetails.ucc,
        isGoogleSignUp,
        isPowerpoint
      });
      localStorage.setItem(`PRESENTATION_SETTINGS_${auth?.id}`, JSON.stringify(getPresentationSettings(true)));
    }

    let path = '/';
    await setAuth(auth);

    setBanner();
    setUniversalLoggedInCookie();
    checkDeepLink();

    const query = new URLSearchParams(router.query as any);
    if (auth?.isNewUser) {
      query.set('newUser', 'true');
    }

    if (isAppSumoUser) {
      path = `/appSumoPricingPlan/${appSumoPlan}`;
    }
    if (isZoomApp()) {
      path = zoomAppBasePath!;
    }
    if (searchParams?.get('invite') && !auth?.isNewUser) {
      path = '/presentations';
    }
    if (isPowerpoint) {
      if (auth?.isNewUser) {
        path = '/interaction-links';
      } else {
        path = await getLastRoutePath();
      }
    }
    if (isPopup) {
      path = router.asPath;
    }

    broadcastAuthState('login');

    await initTeam();

    if (query.has('next')) {
      const next = query.get('next');
      query.delete('next');
      query.delete('email');

      router.push(`${next}?${query.toString()}`);
      return;
    }

    router.push({
      pathname: path,
      query: query.toString(),
    });
  };

  const handleGoogleLogin = async (auth: any) => {
    localStorage.setItem('user_email', auth?.email);
    localStorage.setItem('user_id', auth?.id);
    localStorage.setItem('hash', auth?.hash);
    if (auth?.isNewUser) {
      pushDataLayerForEvent('google_signup_completed', { email: auth.email });
    } else {
      pushDataLayerForEvent('google_login_success', { email: auth.email });
    }
  };

  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      verifyGoogleOneTapToken(credentialResponse.credential)
        .then(async (res) => {
          if (res.status) {
            handleGoogleLogin(res.entity);
            await handleLoginSuccess(res.entity, true);
          }
        })
        .catch((e) => {
          console.error('Error:', e);
        });
    },
    onError: () => {
      console.error('Login Failed');
    },
    use_fedcm_for_prompt: true,
    disabled: !!isZoomApp(),
    cancel_on_tap_outside: true,
  });

  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      verifyGoogleAccessToken(codeResponse.access_token)
        .then(async (res) => {
          if (res.status) {
            handleGoogleLogin(res.entity);
            await handleLoginSuccess(res.entity, true);
          }
        })
        .catch((e) => {
          console.error('Error:', e);
        });
    },
    onError: () => console.error('Login Failed'),
  });

  const startStripeCheckout = async (plan: any, mode: any) => {
    const checkoutDetails = await createCheckoutSession({
      newPlan: plan,
      mode,
      userId: getAuth().id,
    });
    if (checkoutDetails.status) {
      router.replace(checkoutDetails.entity.url);
    }
  };

  const stripeCheckout = async (plan: any) => {
    if (ownTeam?.billing_account?.purchase_plan?.name === 'Free Plan') {
      const mode = plan?.toLowerCase().includes('event')
        ? 'payment'
        : 'subscription';
      await startStripeCheckout(plan, mode);
    } else {
      window.location.replace('/pricing');
    }
  };

  const checkDeepLink = async () => {
    const query = new URLSearchParams(window.location.search);
    if (query.has(ZOOMAPP_CTX_PARAM)) {
      try {
        const deepLinkRes = await getDeeplink({ ctx: query.get(ZOOMAPP_CTX_PARAM) });

        if (deepLinkRes.status && deepLinkRes.entity?.deeplink) {
          return await router.push(deepLinkRes.entity.deeplink);
        }
      } catch (e: any) {
        const url = getDeepLinkErrorRedirectUrl(window.location.href, e.response?.data, getAuth(), query.get(ZOOMAPP_CTX_PARAM));
        url && window.location.replace(url.toString());
      }
    }
  };

  useEffect(() => {
    const auth = getAuth();

    if (isAuthenticated(auth)) {
      (async () => {
        if (router.query[ZOOMAPP_CTX_PARAM]) {
          checkDeepLink();
        }
        await initTeam();
        if (
          router.query?.plan &&
          ownTeam?.billing_account?.purchase_plan?.name
        ) {
          await stripeCheckout(router.query.plan);
        } else if (isZoomApp()) {
          router.push(zoomAppBasePath!);
        }
        if (isPowerpoint) {
          router.push(await getLastRoutePath());
        }
        if (router.query?.next) {
          const next = router.query.next as string;
          router.push({
            pathname: next,
            query: router.query,
          });
        }
      })();
    }
    // Just making sure that zoom users don't end up in normal login page.
    if (isZoomApp()) {
      const matches = router.pathname.match(/(^\/login|^\/signup)/);
      if (matches && matches.length > 0) {
        router.push(`/zoom-app${matches[0]}`);
      }
    }
  }, []);

  useEffect(() => {
    if (router.query?.email) {
      if (router.query.tier) {
        setIsAppSumoUser(true);
        setAppSumoPlan(router.query.tier as string);
      }
    }
  }, [router.query?.email]);

  const handle429 = (e: Error) => {
    if (e.message.match('429')) {
      setToastMsg('Too many requests, please try again after sometime.');
    }
  };

  const getLoginCode = async (
    { email: emailInput }: { email: string },
    onSuccess?: () => void
  ) => {
    const email = emailInput?.toLowerCase();
    try {
      const res = await userLogin({ email, accessType });
      if (accessType === 'signup') {
        pushDataLayerForEvent('signup_started', { email }, false);
      } else {
        pushDataLayerForEvent('login_started', { email }, false);
      }

      if (res.message[0] === 'User already exists.') {
        setFocus('email');
        setCustomError({
          type: 'email',
          message: 'That account exists.',
        });
        return;
      }
      if (res.message[0] === 'No Account Exist') {
        setFocus('email');

        setCustomError({
          type: 'email',
          message: 'No account exists with that email.',
        });
        return;
      }
      if (!res.status) {
        setFocus('email');
        const defaultErrMsg = 'Something went wrong. Please try again.';
        if (Array.isArray(res.message)) {
          setToastMsg(res.message?.[0] || defaultErrMsg);
        } else setToastMsg(res.message || defaultErrMsg);
        return;
      }
      setShowCodeInput(true);
      setMainButtonText('Log In');
      onSuccess?.();
    } catch (e: any) {
      handle429(e);
    }
  };

  const verifyLoginCode = async ({ inputCode }: { inputCode: string }) => {
    setLoader(true);
    const emailValue = getValues('email');
    const email = Array.isArray(emailValue) ? emailValue[0]?.toLowerCase() ?? '' : emailValue?.toLowerCase() ?? '';

    try {
      const res = await verifyLogin({
        email,
        inputCode,
        accessType,
        clientInfo,
      });
      if (res.status) {
        localStorage.setItem('user_email', email);
        localStorage.setItem('user_id', res.entity[0]?.id);
        localStorage.setItem('hash', res.entity[0]?.hash);
        if (accessType === 'signup') {
          // @ts-expect-error will check if variable is defined
          if (window.fpr) {
            // @ts-expect-error variable is defined
            window.fpr('referral', { email });
          }
          pushDataLayerForEvent('signup_completed', { email }, false);
          // @ts-expect-error will check if variable is defined
          if (window.lintrk) {
            // @ts-expect-error  variable is defined
            window.lintrk('track', { conversion_id: 10938372 });
          }
        } else {
          pushDataLayerForEvent('login_success', { email }, false);
        }
        await handleLoginSuccess(res.entity[0]);
      } else {
        codeSetFocus('inputCode');
        setCustomError({
          type: 'code',
          message: 'Code is invalid or expired. Try requesting a new code.',
        });
        setLoader(false);
      }
    } catch (e: any) {
      handle429(e);
      setLoader(false);
    }
  };

  useEffect(() => {
    if (showCodeInput) {
      codeSetFocus('inputCode');
    }
  }, [showCodeInput]);

  return (
    <div className="bg-content2 flex size-full justify-center text-grey-900 dark:text-grey-200 overflow-y-auto">
      <div className="flex flex-1 static h-full max-w-160">
        {toastMsg && (
          <Toast
            severity="error"
            body={toastMsg}
            position="absolute"
            onClose={() => setToastMsg('')}
            autoCloseInterval={5000}
          />
        )}
        <div
          className={`flex flex-1 z-10 ${isZoomApp() && 'tablet:p-12 p-6 md:px-24 md:py-9'} ${!isPopup && !isZoomApp() && 'p-6 tablet:p-12 md:p-16 xl:px-24 lg:py-9'}  overflow-auto`}
        >
          <div
            className={`flex flex-1 flex-col relative ${!isPopup && 'gap-6'}`}
          >
            {isZoomApp() && (
              <div>
                <Button
                  kind="icon-only"
                  size="sm"
                  className="inline-block bg-grey-100 mr-2 rounded-[5px]"
                  onClick={() => router.back()}
                >
                  <ArrowBackIcon size={20} />
                </Button>
                <div className="inline-block">
                  <Image
                    width={144}
                    height={144}
                    alt="streamalive logo"
                    src={'/images/logos/streamalive.svg'}
                    className="w-36 inline-block dark:hidden"
                  />
                  <Image
                    width={144}
                    height={144}
                    alt="streamalive logo"
                    src={'/images/logos/streamalive-white.svg'}
                    className="w-36 hidden dark:inline-block"
                  />
                </div>
              </div>
            )}
            {!isPopup && !isZoomApp() ? (
              <><Image
                width={144}
                height={144}
                alt="streamalive logo"
                src={'/images/logos/streamalive.svg'}
                className="w-36 inline-block dark:hidden"
              />
              <Image
                width={144}
                height={144}
                alt="streamalive logo"
                src={'/images/logos/streamalive-white.svg'}
                className="w-36 hidden dark:inline-block"
              /></>
            ) : (
              <div className="mt-2" />
            )}
            {loader && (
              <Loader
                className="my-auto absolute bg-content2"
              />
            )}
            {!loader &&
              <>{
                teamInvite === 'invalid' ? <>
                  <h4 className={`${isPopup ? 'text-xl mb-2' : 'text-4xl mb-2'} font-semibold mt-[35vh]`}>
                    Invalid Invitation
                  </h4>
                  <p className='text-sm text-grey-700 dark:text-grey-300 mb-6'>
                    This invitation is not valid for {searchParams?.get('invite')}.
                  </p>
                </> : (
                  <>
                    <div className="flex-1 place-self-stretch"></div>
                    <div className="flex flex-col  justify-center">
                      <h4
                        className={`${isPopup ? 'text-xl mb-6 leading-[1.2]' : 'text-4xl mb-6 leading-[1.2]'} font-semibold `}
                      >
                        {teamInvite ? `${accessType === 'login' ? 'Login' : 'Sign up'} to join ${teamInvite?.billing_account?.name} on StreamAlive` : heading}
                      </h4>
                      {teamInvite && (
                        <p className='text-sm text-grey-700 dark:text-grey-300 mb-6'>
                          Once you join, you'll have access to view and edit presentations in the {teamInvite?.billing_account?.name} workspace.
                        </p>
                      )}

                      {!teamInvite && accessType === 'signup' && (
                        <p className='text-sm text-grey-700 dark:text-grey-300 mb-6'>
                          Experience all key features without committing.
                          <br />
                          Main limitation: up to 5 participants per session.
                        </p>
                      )}

                      {!isZoomApp() && !isPowerpointDesktop && (
                        <div>
                          <Button
                            className="w-full"
                            kind={showCodeInput ? 'secondary' : 'primary'}
                            startContent={<GoogleIcon />}
                            onPress={() => googleLogin()}
                          >
                            Continue with Google
                          </Button>
                          <div className="flex items-center my-6">
                            <div className="flex-1 border border-divider"></div>
                            <p className="text-default-600 text-center font-normal text-sm mx-2">
                              or
                            </p>
                            <div className="flex-1 border border-divider"></div>
                          </div>
                        </div>
                      )}

                      {(errors?.email ||
                        customError?.message ||
                        codeErrors?.inputCode) &&
                        (
                          <div className="mb-4 bg-red-50 dark:bg-transparent dark:text-pure-red-600 border border-pure-red-600 px-4 py-3.5 rounded-md flex gap-[15px] items-center">
                            <InfoOutlinedIcon
                              className="text-pure-red-600" size={20}
                            />
                            <span className="text-sm">
                              {(errors?.email?.message!) ||
                                (codeErrors?.inputCode?.message as string) ||
                                customError.message}
                            </span>
                          </div>
                        )}

                      <form
                        onSubmit={
                          showCodeInput
                            ? codeHandleSubmit(verifyLoginCode)
                            : handleSubmit((data) => getLoginCode(data))
                        }
                      >
                        <label className="text-sm font-semibold">Email</label>
                        <Input
                          {...register('email')}
                          disabled={isAppSumoUser}
                          isInvalid={
                            customError.type === 'email' || !!errors?.email
                          }
                          className="mt-2 w-full"
                          size="lg"
                        />

                        {showCodeInput && (
                          <div className="mt-4">
                            <label className="text-sm font-semibold">
                              {accessType === 'login'
                                ? 'Login code'
                                : 'Signup code'}
                            </label>
                            <Input
                              {...codeRegister('inputCode')}
                              maxLength={6}
                              placeholder={`Paste ${accessType} code`}
                              disabled={isAppSumoUser}
                              isInvalid={
                                customError.type === 'code' ||
                                !!codeErrors?.inputCode
                              }
                              className="mt-2 w-full"
                              size="lg"
                            />
                            <p className="text-sm mt-2 text-grey-700 dark:text-grey-300">
                              We sent a {accessType} code to your inbox · &nbsp;
                              {isRunning ? (
                                `${count}s`
                              ) : (
                                <button
                                  type="button"
                                  className="text-blue-600 font-semibold cursor-pointer"
                                  onClick={() => {
                                    const emailValue = getValues('email');
                                    getLoginCode(
                                      { email: Array.isArray(emailValue) ? emailValue[0] ?? '' : emailValue ?? '' },
                                      () => startTimer(30)
                                    );
                                  }}
                                >
                                  Resend
                                </button>
                              )}
                            </p>
                          </div>
                        )}

                        <Button
                          id="login"
                          type="submit"
                          onClick={
                            showCodeInput
                              ? codeHandleSubmit(verifyLoginCode)
                              : handleSubmit((data) => getLoginCode(data))
                          }
                          isLoading={loader}
                          size="md"
                          kind={showCodeInput ? 'primary' : 'secondary'}
                          className="w-full mt-4"
                        >
                          {showCodeInput
                            ? accessType === 'login'
                              ? 'Log in'
                              : 'Create free account'
                            : mainButtonText}
                        </Button>

                        {accessType === 'login' && (
                          <p className="text-sm my-6">
                            <span>New to streamAlive? </span>
                            {isZoomApp() ? (
                              <ExternalLink
                                href={signupLink('zoomapp')}
                                className="text-blue-600 font-semibold cursor-pointer"
                              >
                                Sign up
                              </ExternalLink>
                            ) : (
                              <a
                                onClick={() => {
                                  if (isPopup) {
                                    const signupUrl = '/signup?utm_source=interaction_link&utm_medium=referral';
                                    window.open(signupUrl, '_blank');
                                    return;
                                  }

                                  const query = {
                                    ...router.query,
                                    email,
                                  };

                                  router.push({
                                    pathname: '/signup',
                                    query,
                                  });
                                }}
                                className="text-blue-600 font-semibold cursor-pointer"
                              >
                                Sign up
                              </a>
                            )}

                          </p>
                        )}

                        {accessType === 'signup' && (
                          // @ts-expect-error not tsx
                          <Text className={`text-sm ${isPopup ? 'mt-6' : 'mt-5'}`}>
                            <span>Already have an account? </span>

                            {
                              <a
                                onClick={() => {
                                  const query = {
                                    ...router.query,
                                    email,
                                  };
                                  return router.push({ pathname: (isZoomApp() ? '/zoom-app/login' : '/login'), query });
                                }}
                                className="text-blue-600 text-sm font-semibold cursor-pointer"
                              >
                                Log in
                              </a>
                            }
                          </Text>
                        )}
                      </form>
                      {accessType === 'signup' && (
                        <p
                          className={`${isPopup ? 'pt-6 mt-6' : 'pt-8 my-8'} border-t border-grey-100 dark:border-grey-800 text-grey-700 dark:text-grey-300 text-sm`}
                        >
                          By signing up, you agree to the
                          <Link
                            target="_blank"
                            href="https://www.streamalive.com/terms-of-service"
                            className="text-grey-800 dark:text-neutral-10 font-medium"
                          >
                            {' '}
                            Terms of service{' '}
                          </Link>
                          and
                          <Link
                            target="_blank"
                            href="https://www.streamalive.com/privacy"
                            className="text-grey-800 dark:text-neutral-10 font-medium"
                          >
                            {' '}
                            Privacy policy
                          </Link>
                        </p>
                      )}
                    </div>
                    <div className="flex-1 place-self-stretch"></div>
                  </>
                )
              }
              </>
            }
          </div>
        </div>
      </div>

      {!isPopup && <Banner isSignup={accessType === 'signup'} />}
    </div>
  );
};

interface BannerProps {
  isSignup?: boolean;
}

const Banner = ({ isSignup }: BannerProps) => {
  return (
    <div className="hidden flex-1 login-gradient p-[40px] lg:flex flex-col justify-center items-center">
      {isSignup && (
        <div className="text-left w-full max-w-[680px] mb-12">
          <p className="text-neutral-10 text-2xl font-semibold mb-4">
            Why sign up?
          </p>

          <ul className="flex flex-col text-grey-300 text-lg font-normal gap-2 list-decimal ml-5">
            <li>Crafted for virtual and in-person instructor-led sessions</li>
            <li>Works with PowerPoint, Canva or Gamma, or without slides</li>
            <li>Reads chat in Zoom, Teams, Meet, LinkedIn, YouTube Live, etc</li>
          </ul>
        </div>
      )}

      <div
        className={`overflow-y-scroll relative ${isSignup ? 'max-w-[680px]' : 'w-[586.408px] h-full'} flex items-center justify-center flex-col ${isSignup ? 'bg-[#141414] py-9 px-6 rounded-[16px] border border-grey-900' : ''}`}
      >
        {isSignup && (
          <p className={'font-normal text-neutral-10 tracking-[0.084px] text-center text-lg max-w-[400px] mb-[32px] leading-[27px]'}>
            Used by thousands of <span className="font-semibold">trainers</span>,{' '}
            <span className="font-semibold">facilitators</span>,{' '}
            <span className="font-semibold">instructors</span>,{' '}
            <span className="font-semibold">educators</span>,{' '}
            <span className="font-semibold">streamers</span>,{' and '}
            <span className="font-semibold">businesses</span>,
          </p>
        )}
        {!isSignup && (
          <>
            <h2 className='text-4xl font-semibold text-neutral-10 text-left w-full'>
              <span>Integrate StreamAlive for a</span><br className='m-0 p-0 h-0' />
              <span>seamless experience</span>
            </h2>
            <p className={'font-normal text-neutral-10 mb-6 tracking-[0.084px] text-lg mt-6 border-b border-[rgba(255,255,255,0.30)] pb-6 w-full'}>
              <span>
                Our web app works with Zoom, Teams, Meet, and many other {" "}
              </span>
              <span>
                meeting and streaming platforms.
              </span>
            </p>

            <p className={'font-normal text-neutral-10 mb-6 tracking-[0.084px] text-lg w-full'}>
              Use StreamAlive directly inside PowerPoint and Zoom — no tab <br />
              switching needed.</p>
          </>
        )}
        {isSignup ?
          <ImageSlider />
          :
          <article className='flex w-full'>
            <div className="flex gap-8 justify-start border-r border-[rgba(255,255,255,0.30)] h-[107px] items-center pr-[55px]">
              <a href='https://marketplace.microsoft.com/en-us/product/office/WA200008235?tab=Overview' target='_blank' rel='noopener noreferrer' className='flex items-center flex-col gap-2.5 cursor-pointer'>
                <Image src='/images/logos/powerpoint.svg' alt='powerpoint' width={43} height={43} />
                <p className='font-semibold text-neutral-10'>PowerPoint</p>
              </a>
              <a href='https://marketplace.zoom.us/apps/DyWJwpPIS429BoBLdEsWKw' target='_blank' rel='noopener noreferrer' className='flex items-center flex-col gap-2 cursor-pointer'>
                <Image src='/images/logos/zoom.svg' alt='zoom' width={43} height={43} />
                <p className='font-semibold text-neutral-10'>Zoom</p>
              </a>
            </div>
            <div className='w-full gap-2 ml-[55px]'>
              <p className='text-lg mt-2 text-neutral-10'>UP NEXT:</p>
              <div className='flex items-center gap-4 mt-4'>
                <span className='flex items-center flex-col gap-4'>
                  <Image src='/images/logos/gama.svg' alt='gama' width={104.156} height={18} />
                </span>
                <span className='flex items-center flex-col gap-2'>
                  <Image src='/images/logos/canva.svg' alt='canva' width={72} height={27} />
                </span>
                <span className='flex items-center flex-col gap-2'>
                  <Image src='/images/logos/miro.svg' alt='miro' width={76.295} height={28} />
                </span>
              </div>
            </div>
          </article>
        }
      </div>
      {!isSignup &&
        <article className='flex w-[586.408px] bottom-0 p-6 rounded-md gap-[40px]'
          style={{ background: 'rgba(255, 255, 255, 0.10)' }}
        >
          <a href='https://marketplace.microsoft.com/en-us/product/office/WA200008235?tab=Overview' target='_blank' rel='noopener noreferrer' className='flex gap-2 cursor-pointer'>
            <Image src='/images/logos/microsoft-store.svg' alt='microsoft-store' width={38.75} height={33.75} />
            <div>
              <p className='font-semibold text-neutral-10'>Microsoft app store</p>
              <span className='flex gap-2'>
                <Image src='/images/logos/star.svg' alt='star' width={16} height={16} />
                <Image src='/images/logos/star.svg' alt='star' width={16} height={16} />
                <Image src='/images/logos/star.svg' alt='star' width={16} height={16} />
                <Image src='/images/logos/star.svg' alt='star' width={16} height={16} />
                <Image src='/images/logos/star.svg' alt='star' width={16} height={16} />
              </span>
            </div>
          </a>
          <a href='https://marketplace.zoom.us/apps/DyWJwpPIS429BoBLdEsWKw' target='_blank' rel='noopener noreferrer' className='flex gap-2'>
            <Image src='/images/logos/zoom.svg' alt='zoom' width={40} height={40} />
            <span>
              <p className='font-semibold text-neutral-10'>Zoom app marketplace</p>
              <p className='text-warning'>#1 Featured AI</p>
            </span>
          </a>
        </article>
      }
    </div>
  );
};

export default Login;
